import { FC, useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Badge, Button } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { MatingRecord, Dog } from '@/types/dog';
import { DogIdentityLabel } from '@/components/dog/DogIdentityLabel';
import { dogService } from '@/services/firebase/dogService';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface MatingRecordCardProps {
  matingRecord: MatingRecord;
  userId: string;
  onEdit?: (recordId: string) => void;
  onDelete?: (recordId: string) => void;
}

/**
 * 交配記録カードコンポーネント
 */
export const MatingRecordCard: FC<MatingRecordCardProps> = ({
  matingRecord,
  userId,
  onEdit,
  onDelete,
}) => {
  const [male, setMale] = useState<Dog | null>(null);
  const [female, setFemale] = useState<Dog | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const [maleData, femaleData] = await Promise.all([
          dogService.getDog(matingRecord.maleId, userId),
          dogService.getDog(matingRecord.femaleId, userId),
        ]);

        setMale(maleData);
        setFemale(femaleData);
      } catch (error) {
        console.error('親犬情報取得エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParents();
  }, [matingRecord, userId]);

  if (loading) {
    return (
      <Box p={4} borderRadius="lg" borderWidth={1} bg="white" shadow="sm">
        <Text color="gray.500" fontSize="sm">
          読み込み中...
        </Text>
      </Box>
    );
  }

  return (
    <Box
      p={5}
      borderRadius="lg"
      borderWidth={1}
      bg="white"
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
    >
      <VStack align="stretch" gap={3}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <Text fontWeight="bold" fontSize="lg">
            {format(matingRecord.matingDate.toDate(), 'yyyy年M月d日', { locale: ja })}
          </Text>
          {matingRecord.birthRecordId && (
            <Badge colorPalette="green" fontSize="xs">
              出産記録あり
            </Badge>
          )}
          {!matingRecord.birthRecordId && (
            <Badge colorPalette="gray" fontSize="xs">
              出産待ち
            </Badge>
          )}
        </HStack>

        {/* 親犬情報 */}
        <Box>
          <HStack gap={4} align="flex-start">
            <Box flex={1} minW={0}>
              <Text fontSize="xs" color="gray.600">
                オス犬（父）
              </Text>
              {male ? (
                <DogIdentityLabel dog={male} size="sm" showRegistrationNumbers />
              ) : (
                <Text fontWeight="medium">不明</Text>
              )}
            </Box>
            <Text color="gray.400">×</Text>
            <Box flex={1} minW={0}>
              <Text fontSize="xs" color="gray.600">
                メス犬（母）
              </Text>
              {female ? (
                <DogIdentityLabel dog={female} size="sm" showRegistrationNumbers />
              ) : (
                <Text fontWeight="medium">不明</Text>
              )}
            </Box>
          </HStack>
        </Box>

        {/* 出産予定日 */}
        <Box pt={2} borderTopWidth={1}>
          <Text fontSize="xs" color="gray.600">
            出産予定日（目安）
          </Text>
          <Text fontWeight="medium" fontSize="md">
            {format(matingRecord.expectedBirthDate.toDate(), 'yyyy年M月d日', { locale: ja })}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            交配日+63日
          </Text>
        </Box>

        {/* アクションボタン */}
        {(onEdit || onDelete) && (
          <HStack gap={2} pt={2}>
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(matingRecord.id)} flex={1}>
                編集
              </Button>
            )}
            {onDelete && (
              <DialogRoot
                open={isDeleteDialogOpen}
                onOpenChange={(e) => setIsDeleteDialogOpen(e.open)}
              >
                <DialogTrigger asChild>
                  <Button size="sm" colorPalette="red" variant="outline" flex={1}>
                    削除
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>交配記録の削除</DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <Text>
                      {format(matingRecord.matingDate.toDate(), 'yyyy年M月d日', { locale: ja })}
                      の交配記録を削除してもよろしいですか？
                    </Text>
                    <Text mt={2} fontSize="sm" color="red.600">
                      この操作は取り消せません。
                    </Text>
                  </DialogBody>
                  <DialogFooter>
                    <DialogActionTrigger asChild>
                      <Button variant="outline">キャンセル</Button>
                    </DialogActionTrigger>
                    <Button
                      colorPalette="red"
                      onClick={() => {
                        onDelete(matingRecord.id);
                        setIsDeleteDialogOpen(false);
                      }}
                    >
                      削除
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </DialogRoot>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};
